// Writing Settings. The owner has one record (the well-known id). An admin
// member (2026-10-17) has one of her own, with an id and label the server made
// from her grant, holding only her look and layout, so what she changes never
// reaches his. Every writer of Settings goes through here, so none of them has
// to know which it is.
import { db, mutate } from '@/runtime'
import { MEMBER_LABEL, MEMBER_SETTINGS_KEYS, type SettingsSpec } from '@/kinds/settings/schema'
import { settingsIdNow, useAccess } from '@/state/access'

/** The id this device's Settings are kept under. */
export const mySettingsId = settingsIdNow

const labelsNow = (): Record<string, string> => {
  const a = useAccess.getState()
  return a.admin && a.grant ? { [MEMBER_LABEL]: a.grant } : {}
}

/** Patch this device's Settings, making the record on the first write. */
export const patchSettings = async (patch: Record<string, unknown>) => {
  const id = settingsIdNow()
  if (await db.resources.get(id)) await mutate.patch(id, { spec: patch })
  else await mutate.create('Settings', patch, labelsNow(), id)
}

/** What a device kept in `settings-cache` before the member was an admin: her look and layout carry over to her own record. */
const carriedOver = (): Record<string, unknown> => {
  try {
    const cached = JSON.parse(localStorage.getItem('settings-cache') ?? '{}') as Record<string, unknown>
    return Object.fromEntries(
      MEMBER_SETTINGS_KEYS.filter((k) => cached[k] !== undefined).map((k) => [k, cached[k]]),
    )
  } catch {
    return {}
  }
}

/** Make sure the Settings record exists so preferences sync everywhere (the owner's, or an admin member's own). */
export const ensureSettings = async () => {
  const id = settingsIdNow()
  if (await db.resources.get(id)) return
  const a = useAccess.getState()
  await mutate.create('Settings', a.admin ? carriedOver() : {}, labelsNow(), id).catch(() => {})
}

export type SettingsPatch = Partial<SettingsSpec>
